import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { useToast } from "@/components/ui/use-toast";
import { useConfirmDialog, useI18n } from "@/context";
import { useProduct } from "@/pages/products/products.hooks";
import { EProductType } from "@/pages/products/products.type";
import { useUsers } from "@/pages/users/users.hooks";

import {
  clientProductsFormSchema,
  clientProductsFormSchemaSearch,
  TClientProductsFormData,
  TClientProductsFormSearchData,
} from "../clients/clients.schema";
import { TClient, TClientExtended } from "../clients/clients.types";
import * as plansApi from "../clients/plans.api";
import { TPlanForBackend, TPlanUpdate } from "../clients/plans.types";
import { IUseProjectPlans } from "./project-plans.hooks.interface";
import * as projectsApi from "./projects.api";

export const useProjectPlans = (
  selectedClient: TClient | TClientExtended | null,
  projectId: string | undefined,
  projectType?: "affinity" | "affiliate"
): IUseProjectPlans => {
  const { t } = useI18n();
  const { toast } = useToast();
  const { openDialog } = useConfirmDialog();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const params = useParams<{ clientId?: string }>();
  // Hooks externos
  const { products } = useProduct();
  const { users } = useUsers();

  // Estados para gerenciamento de planos
  const [tabs, setTabs] = React.useState<{ id: string; label: string }[]>([]);
  const [newTabName, setNewTabName] = React.useState("");
  const [activeTab, setActiveTab] = React.useState("tab1");

  // Estados para filtragem
  const [filteredProducts, setFilteredProducts] = React.useState(
    products || []
  );
  const [filteredUsers, setFilteredUsers] = React.useState(users || []);

  const clientProductsForm = useForm<TClientProductsFormData>({
    resolver: zodResolver(clientProductsFormSchema),
    defaultValues: {
      planos: [],
    },
  });

  const clientProductsFormSearch = useForm<TClientProductsFormSearchData>({
    resolver: zodResolver(clientProductsFormSchemaSearch),
    defaultValues: {
      product_name: "",
      member_name: "",
    },
  });

  // Funções de filtragem
  const handleSearchProducts = React.useCallback(() => {
    const searchTerm = clientProductsFormSearch
      .getValues("product_name")
      .toLowerCase()
      .trim();

    if (!searchTerm) {
      setFilteredProducts(products || []);
      return;
    }

    const filtered = (products || []).filter((product) =>
      product.name?.toLowerCase().includes(searchTerm)
    );
    setFilteredProducts(filtered);
  }, [products, clientProductsFormSearch]);

  const handleSearchUsers = React.useCallback(() => {
    const searchTerm = clientProductsFormSearch
      .getValues("member_name")
      .toLowerCase()
      .trim();

    if (!searchTerm) {
      setFilteredUsers(users || []);
      return;
    }

    const filtered = (users || []).filter((user) =>
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm)
    );
    setFilteredUsers(filtered);
  }, [users, clientProductsFormSearch]);

  const clearProductsFilter = React.useCallback(() => {
    setFilteredProducts(products || []);
  }, [products]);

  const clearUsersFilter = React.useCallback(() => {
    setFilteredUsers(users || []);
  }, [users]);

  // Funções para gerenciamento de planos
  const handleAddTab = React.useCallback((): { id: string; label: string } => {
    if (!newTabName.trim()) return { id: "", label: "" };

    const newId = `tab${tabs.length + 1}`;
    const newTab = { id: newId, label: newTabName };
    setTabs([...tabs, newTab]);
    setActiveTab(newId);
    setNewTabName("");
    return newTab;
  }, [newTabName, tabs]);

  const addTabToForm = React.useCallback(() => {
    if (!newTabName.trim()) return;
    const newTab = handleAddTab();
    if (!newTab.id) return;

    const currentPlanos = clientProductsForm.getValues("planos") || [];
    const newPlanoIndex = currentPlanos.length;

    const newPlano = {
      id: newTab.id,
      name: newTab.label,
      description: "",
      plan_features: [],
      plan_price: 0,
      products: [] as Array<{
        id: string;
        capital_value?: number;
        premium_value?: number;
        description?: string;
      }>,
      commission_distribution: [{ agency: 0, commission: 0, position: 0, markup: 0, markup_mode: 'percentage' }],
    };

    clientProductsForm.setValue("planos", [...currentPlanos, newPlano], {
      shouldDirty: true,
      shouldTouch: true,
    });

    clientProductsForm.setValue(`planos.${newPlanoIndex}.name`, newTab.label, {
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [newTabName, handleAddTab, clientProductsForm]);

  const deleteTabFromForm = React.useCallback(
    (tabId: string) => {
      const index = tabs.findIndex((t) => t.id === tabId);
      setTabs(tabs.filter((tab) => tab.id !== tabId));

      if (index !== -1) {
        const currentPlanos = clientProductsForm.getValues("planos") || [];
        const newPlanos = [...currentPlanos];
        newPlanos.splice(index, 1);
        clientProductsForm.setValue("planos", newPlanos, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    },
    [tabs, clientProductsForm]
  );

  const handleUpdateTabLabel = React.useCallback(
    (tabId: string, newLabel: string) => {
      setTabs(
        tabs.map((tab) =>
          tab.id === tabId ? { ...tab, label: newLabel } : tab
        )
      );
    },
    [tabs]
  );

  // Atualizar produtos filtrados quando products mudar
  React.useEffect(() => {
    setFilteredProducts(products || []);
  }, [products]);

  // Atualizar usuários filtrados quando users mudar
  React.useEffect(() => {
    setFilteredUsers(users || []);
  }, [users]);

  const createPlanMutation = useMutation({
    mutationFn: async (data: TPlanForBackend[] | any[]) => {
      if (!projectId) {
        throw new Error("Projeto inválido");
      }
      
      // Para criação de planos dentro de projeto (affinity/affiliate), usar sempre a rota nova:
      // POST /organizations/projects/:projectId/plans
      // (a rota antiga POST /organizations/:id/plans não existe no backend atual e cai em /organizations/:projectId/:fatherOrganizationId)
      return projectsApi.createPlansInProject(projectId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      queryClient.invalidateQueries({ queryKey: ["plans", selectedClient?.id] });
      toast({
        title: t("generic.success"),
        description: "Planos criados e associados ao projeto com sucesso!",
      });
      // Limpar formulário
      clientProductsForm.reset();
      setTabs([]);
      setActiveTab("tab1");
      // Redirecionar para a página de detalhes do projeto
      const clientIdToUse = selectedClient?.id || params.clientId;
      if (clientIdToUse && projectId) {
        navigate(`/clients/${clientIdToUse}/projects/${projectId}`);
      }
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message || "Erro ao criar planos",
        variant: "destructive",
      });
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: async (payload: { planId: string; data: TPlanUpdate; projectId?: string }) => {
      // Para planos dentro de projeto (affinity/affiliate), usar a rota de projetos
      if (payload.projectId) {
        // organizationId não é usado pela rota nova (vai no header automaticamente pelo customFetch)
        return projectsApi.updatePlanInProject("", payload.projectId, payload.planId, payload.data);
      }
      
      // Caso contrário, usar a rota padrão
      if (!selectedClient?.id) {
        throw new Error("Cliente inválido");
      }
      return plansApi.updatePlan(selectedClient.id, payload.planId, payload.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      queryClient.invalidateQueries({ queryKey: ["plans", selectedClient?.id] });
      queryClient.invalidateQueries({ queryKey: ["plan"] });
      toast({ title: t("generic.success"), description: t("clients.success.planSaved") });
      // Redirecionar para a página de detalhes do projeto
      const clientIdToUse = selectedClient?.id || params.clientId;
      if (clientIdToUse && projectId) {
        navigate(`/clients/${clientIdToUse}/projects/${projectId}`);
      }
    },
    onError: (error: Error) => {
      toast({ title: t("generic.error"), description: error.message, variant: "destructive" });
    },
  });

  const handleSubmitCreatePlan = React.useCallback(
    (data: TClientProductsFormData) => {
      if (!projectId) {
        toast({
          title: t("generic.error"),
          description: "ID do projeto não encontrado",
          variant: "destructive",
        });
        return;
      }

      if (!data.planos || data.planos.length === 0) {
        toast({
          title: t("generic.error"),
          description: "Nenhum plano encontrado para cadastrar",
          variant: "destructive",
        });
        return;
      }

      if (Array.isArray(data.planos)) {
        // Se for projeto de afinidade ou afiliado, usar o novo formato
        if (projectType === "affinity" || projectType === "affiliate") {
          const plansData: any[] = [];
          
          for (const plano of data.planos) {
            // Buscar dados de installments e otherInstallments do plano
            // O formulário pode salvar installments e otherInstallments diretamente ou via selectedPartners
            const installments = (plano as any).installments || [];
            const otherInstallments = (plano as any).otherInstallments;
            const selectedPartners = (plano as any).selectedPartners || {};
            
            // Transformar installments para commission_distribution
            const mainPartnerCommissions: Array<{
              organization_id: string;
              position: number;
              agency: number;
              commission: number;
            }> = [];
            const partnerParticipantCommissions: Array<{
              organization_id: string;
              position: number;
              agency: number;
              commission: number;
            }> = [];
            const hunterCommissions: Array<{
              organization_id: string;
              position: number;
              agency: number;
              commission: number;
            }> = [];
            
            // Processar installments (parcelas)
            if (installments.length > 0) {
              installments.forEach((installment: any, index: number) => {
                if (installment.mainPartner && installment.mainPartner.id) {
                  const agencyValue = installment.mainPartner.agency !== undefined ? installment.mainPartner.agency : 0;
                  const commissionValue = installment.mainPartner.commission !== undefined ? installment.mainPartner.commission : 0;
                  mainPartnerCommissions.push({
                    organization_id: installment.mainPartner.id,
                    position: installment.position !== undefined ? installment.position : index,
                    agency: Math.round(agencyValue * 100),
                    commission: Math.round(commissionValue * 100),
                  });
                }
                
                if (installment.participatingPartners && Array.isArray(installment.participatingPartners)) {
                  installment.participatingPartners.forEach((partner: any, pIndex: number) => {
                    if (partner.id) {
                      const agencyValue = partner.agency !== undefined ? partner.agency : 0;
                      const commissionValue = partner.commission !== undefined ? partner.commission : 0;
                      partnerParticipantCommissions.push({
                        organization_id: partner.id,
                        position: installment.position !== undefined ? installment.position : index,
                        agency: Math.round(agencyValue * 100),
                        commission: Math.round(commissionValue * 100),
                      });
                    }
                  });
                }
                
                // Processar hunters das parcelas
                if (installment.hunters && Array.isArray(installment.hunters)) {
                  installment.hunters.forEach((hunter: any) => {
                    if (hunter.id) {
                      const agencyValue = hunter.agency !== undefined ? hunter.agency : 0;
                      const commissionValue = hunter.commission !== undefined ? hunter.commission : 0;
                      hunterCommissions.push({
                        organization_id: hunter.id,
                        position: installment.position !== undefined ? installment.position : index,
                        agency: Math.round(agencyValue * 100),
                        commission: Math.round(commissionValue * 100),
                      });
                    }
                  });
                }
              });
            }
            
            // Processar otherInstallments (demais parcelas)
            const mainPartnerAnother: Array<{
              organization_id: string;
              markup: number;
              commission: number;
            }> = [];
            const partnerParticipantAnother: Array<{
              organization_id: string;
              markup: number;
              commission: number;
            }> = [];
            const hunterAnother: Array<{
              organization_id: string;
              markup: number;
              commission: number;
            }> = [];
            
            if (otherInstallments) {
              if (otherInstallments.mainPartner && otherInstallments.mainPartner.id) {
                const markupValue = otherInstallments.mainPartner.markup !== undefined ? otherInstallments.mainPartner.markup : 0;
                const commissionValue = otherInstallments.mainPartner.commission !== undefined ? otherInstallments.mainPartner.commission : 0;
                mainPartnerAnother.push({
                  organization_id: otherInstallments.mainPartner.id,
                markup: Math.round(markupValue),
                  commission: Math.round(commissionValue * 100),
                });
              }
              
              if (otherInstallments.participatingPartners && Array.isArray(otherInstallments.participatingPartners)) {
                otherInstallments.participatingPartners.forEach((partner: any) => {
                  if (partner.id) {
                    const markupValue = partner.markup !== undefined ? partner.markup : 0;
                    const commissionValue = partner.commission !== undefined ? partner.commission : 0;
                    partnerParticipantAnother.push({
                      organization_id: partner.id,
                      markup: Math.round(markupValue),
                      commission: Math.round(commissionValue * 100),
                    });
                  }
                });
              }
              
              // Processar hunters das demais parcelas
              if (otherInstallments.hunters && Array.isArray(otherInstallments.hunters)) {
                otherInstallments.hunters.forEach((hunter: any) => {
                  if (hunter.id) {
                    const markupValue = hunter.markup !== undefined ? hunter.markup : 0;
                    const commissionValue = hunter.commission !== undefined ? hunter.commission : 0;
                    hunterAnother.push({
                      organization_id: hunter.id,
                      markup: Math.round(markupValue),
                      commission: Math.round(commissionValue * 100),
                    });
                  }
                });
              }
            }
            
            // Se ainda não há main_partner, usar o selectedClient como fallback
            // O selectedClient é o projectFather (parceiro principal do projeto)
            if (mainPartnerCommissions.length === 0 && selectedClient?.id) {
              mainPartnerCommissions.push({
                organization_id: selectedClient.id,
                position: 0,
                agency: 0,
                commission: 0,
              });
            }
            
            // Processar partner_affiliate se for projeto afiliado
            const partnerAffiliateCommissions: Array<{
              level: number;
              position: number;
              agency: number;
              commission: number;
            }> = [];
            const partnerAffiliateAnother: Array<{
              level: number;
              markup: number;
              commission: number;
            }> = [];
            
            if (projectType === "affiliate") {
              // Processar affiliateCommissions das parcelas
              const affiliateCommissions = (plano as any).affiliateCommissions || {};
              
              Object.keys(affiliateCommissions).forEach((installmentIndexStr) => {
                const installmentIndex = parseInt(installmentIndexStr, 10);
                const installment = installments[installmentIndex];
                if (installment) {
                  const levelCommissions = affiliateCommissions[installmentIndex] || {};
                  Object.keys(levelCommissions).forEach((levelStr) => {
                    const level = parseInt(levelStr, 10);
                    const commission = levelCommissions[level];
                    // Incluir mesmo se os valores forem 0, pois o nível pode ter comissão definida
                    // O backend precisa saber quais níveis têm comissões configuradas
                    if (commission !== undefined && commission !== null) {
                      const affiliateCommission = {
                        level,
                        position: installment.position !== undefined ? installment.position : installmentIndex,
                        agency: Math.round((commission.agency || 0) * 100),
                        commission: Math.round((commission.commission || 0) * 100),
                      };
                      partnerAffiliateCommissions.push(affiliateCommission);
                    }
                  });
                }
              });
              
              // Processar affiliateOtherCommissions (demais parcelas)
              const affiliateOtherCommissions = (plano as any).affiliateOtherCommissions || {};
              
              Object.keys(affiliateOtherCommissions).forEach((levelStr) => {
                const level = parseInt(levelStr, 10);
                const commission = affiliateOtherCommissions[level];
                // Incluir mesmo se os valores forem 0, pois o nível pode ter comissão definida
                // O backend precisa saber quais níveis têm comissões configuradas
                if (commission !== undefined && commission !== null) {
                  const affiliateOther = {
                    level,
                    markup: Math.round((commission.markup || 0) * 100),
                    commission: Math.round((commission.commission || 0) * 100),
                  };
                  partnerAffiliateAnother.push(affiliateOther);
                }
              });
            }
            
            // Validar que temos pelo menos um main_partner antes de adicionar ao array
            if (mainPartnerCommissions.length === 0) {
              toast({
                title: t("generic.error"),
                description: `Plano "${plano.name}" não possui parceiro principal configurado. Este plano será ignorado.`,
                variant: "destructive",
              });
              continue; // Pula este plano e continua com o próximo
            }
            
            plansData.push({
              name: plano.name || "",
              description: plano.description || "",
              products: (plano.products || []).map((product) => ({
                product_id: product.product_id || product.id,
                name: product.name || "",
                type: product.type || "",
                capital_value: product.capital_value || 0,
                premium_value: product.premium_value || 0,
                description: product.description || "",
                insurance_policy_extra_price_id: product.insurance_policy_extra_price_id || null,
              })),
              commission_distribution: {
                main_partner: mainPartnerCommissions,
                partner_participant: partnerParticipantCommissions,
                partner_affiliate: partnerAffiliateCommissions,
                ...(hunterCommissions.length > 0 && { hunter: hunterCommissions }),
              },
              commission_another: {
                main_partner: mainPartnerAnother,
                partner_participant: partnerParticipantAnother,
                partner_affiliate: partnerAffiliateAnother,
                ...(hunterAnother.length > 0 && { hunter: hunterAnother }),
              },
              plan_price: plano.plan_price || 0,
              plan_features: Array.isArray(plano.plan_features) 
                ? plano.plan_features.flat().filter(feature => typeof feature === 'string' && feature.trim() !== '')
                : [],
            });
          }
          
          // Só enviar se houver pelo menos um plano válido
          if (plansData.length > 0) {
            createPlanMutation.mutate(plansData);
          } else {
            toast({
              title: t("generic.error"),
              description: "Nenhum plano válido para criar. Verifique se há pelo menos um parceiro principal configurado.",
              variant: "destructive",
            });
          }
        } else {
          // Formato antigo para projetos affiliate ou outros
          const plansData: TPlanForBackend[] = data.planos.map((plano) => {
            return {
              organization_id: selectedClient?.id || "",
              name: plano.name || "",
              description: plano.description || "",
              products: (plano.products || []).map((product) => {
                return {
                  product_id: product.product_id || product.id,
                  name: product.name || "",
                  type: product.type || "",
                  capital_value: product.capital_value || 0,
                  premium_value: product.premium_value || 0,
                  description: product.description || "",
                };
              }),
              commission_distribution: (plano.commission_distribution || []).map(
                (commission) => {
                  const processedCommission = {
                    agency: Math.round((commission.agency || 0) * 100),
                    commission: Math.round((commission.commission || 0) * 100),
                    position: Math.round(commission.position || 0),
                  };
                  return processedCommission;
                }
              ),
              markup: Math.round((plano.markup || 0)),
              markup_mode: plano.markup_mode || 'percentage',
              commission_lifetime_other_installments: Math.round((plano.commission_lifetime_other_installments || 0) * 100),
              plan_price: plano.plan_price || 0,
              plan_features: Array.isArray(plano.plan_features) 
                ? plano.plan_features.flat().filter(feature => typeof feature === 'string' && feature.trim() !== '')
                : [],
            };
          });

          createPlanMutation.mutate(plansData as any);
        }
      }
    },
    [createPlanMutation, selectedClient, projectId, projectType, toast, t]
  );

  const handleSubmitUpdatePlan = React.useCallback(
    (index: number) => {
      const planos = clientProductsForm.getValues("planos") || [];
      const plano = planos[index];
      
      if (!plano || !plano.id) {
        return;
      }
      
      const processedFeatures = Array.isArray(plano.plan_features) 
        ? plano.plan_features.flat().filter(feature => typeof feature === 'string' && feature.trim() !== '')
        : [];
      
      // Para projetos de afinidade e afiliados, usar o formato novo com commission_distribution e commission_another
      if (projectType === "affinity" || projectType === "affiliate") {
        const installments = plano.installments || [];
        const otherInstallments = plano.otherInstallments;
        
        // Transformar installments em commission_distribution
        const mainPartnerCommissions: Array<{
          organization_id: string;
          position: number;
          agency: number;
          commission: number;
        }> = [];
        const partnerParticipantCommissions: Array<{
          organization_id: string;
          position: number;
          agency: number;
          commission: number;
        }> = [];
        
        installments.forEach((inst: any) => {
          if (inst.mainPartner && inst.mainPartner.id) {
            mainPartnerCommissions.push({
              organization_id: inst.mainPartner.id,
              position: inst.position || 0,
              agency: Math.round((inst.mainPartner.agency || 0) * 100),
              commission: Math.round((inst.mainPartner.commission || 0) * 100),
            });
          }
          
          if (inst.participatingPartners && Array.isArray(inst.participatingPartners)) {
            inst.participatingPartners.forEach((partner: any) => {
              if (partner.id) {
                partnerParticipantCommissions.push({
                  organization_id: partner.id,
                  position: inst.position || 0,
                  agency: Math.round((partner.agency || 0) * 100),
                  commission: Math.round((partner.commission || 0) * 100),
                });
              }
            });
          }
        });
        
        // Transformar otherInstallments em commission_another
        const mainPartnerAnother: Array<{
          organization_id: string;
          markup: number;
          commission: number;
        }> = [];
        const partnerParticipantAnother: Array<{
          organization_id: string;
          markup: number;
          commission: number;
        }> = [];
        
        if (otherInstallments) {
          if (otherInstallments.mainPartner && otherInstallments.mainPartner.id) {
            mainPartnerAnother.push({
              organization_id: otherInstallments.mainPartner.id,
              markup: Math.round(otherInstallments.mainPartner.markup || 0),
              commission: Math.round((otherInstallments.mainPartner.commission || 0) * 100),
            });
          }
          
          if (otherInstallments.participatingPartners && Array.isArray(otherInstallments.participatingPartners)) {
            otherInstallments.participatingPartners.forEach((partner: any) => {
              if (partner.id) {
                partnerParticipantAnother.push({
                  organization_id: partner.id,
                  markup: Math.round(partner.markup || 0),
                  commission: Math.round((partner.commission || 0) * 100),
                });
              }
            });
          }
        }
        
        // Garantir que há pelo menos um main_partner
        if (mainPartnerCommissions.length === 0 && selectedClient?.id) {
          mainPartnerCommissions.push({
            organization_id: selectedClient.id,
            position: 0,
            agency: 0,
            commission: 0,
          });
        }

        // Processar partner_affiliate se for projeto afiliado
        const partnerAffiliateCommissions: Array<{
          level: number;
          position: number;
          agency: number;
          commission: number;
        }> = [];
        const partnerAffiliateAnother: Array<{
          level: number;
          markup: number;
          commission: number;
        }> = [];

        if (projectType === "affiliate") {
          const affiliateCommissions = (plano as any).affiliateCommissions || {};
          Object.keys(affiliateCommissions).forEach((installmentIndexStr) => {
            const installmentIndex = parseInt(installmentIndexStr, 10);
            const installment = installments[installmentIndex];
            if (!installment) return;

            const levelCommissions = affiliateCommissions[installmentIndex] || {};
            Object.keys(levelCommissions).forEach((levelStr) => {
              const level = parseInt(levelStr, 10);
              const commission = levelCommissions[level];
              if (commission === undefined || commission === null) return;
              partnerAffiliateCommissions.push({
                level,
                position:
                  installment.position !== undefined
                    ? installment.position
                    : installmentIndex,
                agency: Math.round((commission.agency || 0) * 100),
                commission: Math.round((commission.commission || 0) * 100),
              });
            });
          });

          const affiliateOtherCommissions = (plano as any).affiliateOtherCommissions || {};
          Object.keys(affiliateOtherCommissions).forEach((levelStr) => {
            const level = parseInt(levelStr, 10);
            const commission = affiliateOtherCommissions[level];
            if (commission === undefined || commission === null) return;
            partnerAffiliateAnother.push({
              level,
              markup: Math.round((commission.markup || 0) * 100),
              commission: Math.round((commission.commission || 0) * 100),
            });
          });
        }
        
        const payload: any = {
          name: plano.name || "",
          description: plano.description || "",
          products: (plano.products || []).map((p: any) => ({
            product_id: p.product_id || p.id,
            name: p.name || "",
            type: p.type || "",
            capital_value: p.capital_value || 0,
            premium_value: p.premium_value || 0,
            description: p.description || "",
            insurance_policy_extra_price_id: p.insurance_policy_extra_price_id || null,
            ...(p.type === EProductType.INSURANCE_ASSISTANCE && p.plan_name
              ? { plan_name: p.plan_name }
              : {}),
            ...(p.type === EProductType.INSURANCE_ASSISTANCE &&
            p.minimum_price !== null &&
            p.minimum_price !== undefined
              ? { minimum_price: p.minimum_price }
              : {}),
          })),
          commission_distribution: {
            main_partner: mainPartnerCommissions,
            partner_participant: partnerParticipantCommissions,
            partner_affiliate: partnerAffiliateCommissions,
          },
          commission_another: {
            main_partner: mainPartnerAnother,
            partner_participant: partnerParticipantAnother,
            partner_affiliate: partnerAffiliateAnother,
          },
          plan_price: plano.plan_price || 0,
          plan_features: processedFeatures,
          soulhub_anchor_product_id: null,
        };
        
        updatePlanMutation.mutate({ planId: String(plano.id), data: payload, projectId });
      } else {
        // Formato antigo para projetos affiliate ou outros
        const payload: TPlanUpdate = {
          name: plano.name || "",
          description: plano.description || "",
          products: (plano.products || []).map((p: any) => ({
            product_id: p.product_id || p.id,
            name: p.name || "",
            type: p.type || "",
            capital_value: p.capital_value || 0,
            premium_value: p.premium_value || 0,
            description: p.description || "",
            insurance_policy_extra_price_id: p.insurance_policy_extra_price_id || null,
            ...(p.type === EProductType.INSURANCE_ASSISTANCE && p.plan_name
              ? { plan_name: p.plan_name }
              : {}),
            ...(p.type === EProductType.INSURANCE_ASSISTANCE &&
            p.minimum_price !== null &&
            p.minimum_price !== undefined
              ? { minimum_price: p.minimum_price }
              : {}),
          })),
          commission_distribution: (plano.commission_distribution || []).map(
            (c: any) => {
              const processedCommission = {
                agency: Math.round((c.agency || 0) * 100), 
                commission: Math.round((c.commission || 0) * 100), 
                position: Math.round(c.position || 0),
              };
              return processedCommission;
            }
          ),
          markup: Math.round((plano.markup || 0)),
          markup_mode: plano.markup_mode || 'percentage',
          commission_lifetime_other_installments: Math.round((plano.commission_lifetime_other_installments || 0) * 100),
          plan_price: plano.plan_price || 0,
          plan_features: processedFeatures,
          soulhub_anchor_product_id: null,
        };
        
        updatePlanMutation.mutate({ planId: String(plano.id), data: payload });
      }
    },
    [clientProductsForm, selectedClient, updatePlanMutation, projectType, projectId]
  );

  const deletePlanMutation = useMutation({
    mutationFn: (planId: string) => {
      if (!selectedClient?.id) {
        throw new Error("Cliente inválido");
      }
      
      // Se for projeto, usar a rota de projetos
      if (projectId) {
        return projectsApi.deletePlanInProject(selectedClient.id, projectId, planId);
      }
      
      // Caso contrário, usar a rota padrão
      return plansApi.deletePlan(selectedClient.id, planId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      queryClient.invalidateQueries({ queryKey: ["plans", selectedClient?.id] });
      toast({
        title: t("generic.success"),
        description: "Plano deletado com sucesso!",
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message || "Erro ao deletar plano",
        variant: "destructive",
      });
    },
  });

  const handleEditPlan = React.useCallback(
    async (
      planId: string,
      projectData?: {
        type_client?: string;
        partner?: any[];
        organization_participants?: any[];
        level_hierarchical?: number;
        hunter?: any[];
        selectedCapitalRanges?: any[];
      }
    ) => {
      if (!selectedClient?.id || !projectId) return;
      
      try {
        // Buscar dados do plano para obter os produtos
        const planData = await projectsApi.getPlanWithCommissions(planId, selectedClient.id);
        
        // Transformar produtos do plano em selectedProducts e selectedCapitalRanges
        const selectedProducts: any[] = [];
        const selectedCapitalRanges: any[] = [];

        const resolveRangeDetails = (planProduct: any) => {
          const baseProduct = (products || []).find(
            (p) => p.id === planProduct.product_id || p.id === planProduct.id
          );
          const ranges = baseProduct?.capital_ranges || [];
          if (!ranges.length) {
            return {
              plan_name: planProduct.plan_name || undefined,
              minimum_price: planProduct.minimum_price ?? null,
            };
          }

          let match: any = null;
          if (planProduct.type === EProductType.INSURANCE_ASSISTANCE) {
            const description = planProduct.description || "";
            const planName = planProduct.plan_name || "";
            const minimumPrice = planProduct.minimum_price;

            match = ranges.find((range) => {
              const descriptionMatch = description
                ? (range.description || "") === description
                : true;
              const planNameMatch = planName
                ? ((range as any).plan_name || "") === planName
                : true;
              const minimumPriceMatch =
                minimumPrice != null
                  ? Number(range.minimum_price || 0) === Number(minimumPrice)
                  : true;

              return descriptionMatch && planNameMatch && minimumPriceMatch;
            });

            if (!match && description) {
              match = ranges.find(
                (range) => (range.description || "") === description
              );
            }

            if (!match && planName) {
              match = ranges.find(
                (range) => ((range as any).plan_name || "") === planName
              );
            }
          } else {
            match = ranges.find((range) => {
              const capitalMatch =
                Number(range.capital_value || 0) ===
                Number(planProduct.capital_value || 0);
              const premiumMatch =
                Number(range.premium_value || 0) ===
                Number(planProduct.premium_value || 0);
              return capitalMatch && premiumMatch;
            });
          }

          return {
            plan_name:
              planProduct.plan_name ||
              (match as any)?.plan_name ||
              undefined,
            minimum_price: planProduct.minimum_price ?? match?.minimum_price ?? null,
          };
        };
        
        if (planData?.products && Array.isArray(planData.products)) {
          planData.products.forEach((product: any) => {
            const resolvedRange = resolveRangeDetails(product);
            const resolvedPlanName =
              resolvedRange.plan_name || product.name || "";
            const resolvedMinimumPrice = resolvedRange.minimum_price ?? null;

            // Se o produto tem product_id diferente do id, significa que é um capital range
            if (product.product_id && product.product_id !== product.id) {
              selectedCapitalRanges.push({
                id: product.id,
                product_id: product.product_id,
                capital_value: product.capital_value || 0,
                premium_value: product.premium_value || 0,
                description: product.description || "",
                plan_name: resolvedPlanName,
                minimum_price: resolvedMinimumPrice,
                insurance_policy_extra_price_id:
                  product.insurance_policy_extra_price_id ?? null,
              });
              
              selectedProducts.push({
                id: product.id,
                product_id: product.product_id,
                name: resolvedPlanName,
                type: product.type || "",
                capital_value: product.capital_value || 0,
                premium_value: product.premium_value || 0,
                description: product.description || "",
                minimum_price: resolvedMinimumPrice,
                plan_name: resolvedPlanName,
                insurance_policy_extra_price_id:
                  product.insurance_policy_extra_price_id ?? null,
              });
            } else {
              // Produto principal (sem capital range específico)
              selectedProducts.push({
                id: product.id || product.product_id,
                product_id: product.product_id || product.id,
                name: resolvedPlanName,
                type: product.type || "",
                capital_value: product.capital_value || 0,
                premium_value: product.premium_value || 0,
                description: product.description || "",
                minimum_price: resolvedMinimumPrice,
                plan_name: resolvedPlanName,
                insurance_policy_extra_price_id:
                  product.insurance_policy_extra_price_id ?? null,
              });
            }
          });
        }
        
        // Redirecionar diretamente para plan-form-page com os produtos
        navigate(`/clients/${selectedClient.id}/plan-form`, {
          state: {
            planId: planId,
            projectId: projectId,
            returnPath: `/clients/${selectedClient.id}/projects/${projectId}`, // Voltar para detalhes do projeto
            isEditing: true,
            selectedProducts: selectedProducts,
            selectedCapitalRanges: selectedCapitalRanges,
            projectType: projectData?.type_client || projectType,
            projectPartners: projectData?.partner,
            projectParticipantOrganizations: projectData?.organization_participants,
            projectLevelHierarchical: projectData?.level_hierarchical,
            projectHunters: projectData?.hunter || [],
            projectSelectedCapitalRanges: projectData?.selectedCapitalRanges || [],
            projectFather: selectedClient ? { id: selectedClient.id, name: selectedClient.name } : undefined,
          },
        });
      } catch (error) {
        toast({
          title: t("generic.error"),
          description: error instanceof Error ? error.message : "Erro ao carregar dados do plano",
          variant: "destructive",
        });
      }
    },
    [navigate, selectedClient, projectId, projectType, toast, t, products]
  );

  const handleDeletePlanWithConfirmation = React.useCallback(
    (planId: string, planName?: string) => {
      if (!selectedClient?.id) return;
      openDialog(
        t("generic.confirmDelete"),
        planName || "",
        () => {
          deletePlanMutation.mutate(planId);
        }
      );
    },
    [selectedClient, openDialog, t, deletePlanMutation]
  );

  return {
    // Estados para gerenciamento de planos
    tabs,
    newTabName,
    activeTab,

    // Estados de filtragem
    filteredProducts,
    filteredUsers,

    // Formulários
    clientProductsForm,
    clientProductsFormSearch,

    // Funções de filtragem
    handleSearchProducts,
    handleSearchUsers,
    clearProductsFilter,
    clearUsersFilter,

    // Funções para gerenciamento de planos
    setNewTabName,
    setActiveTab,
    handleAddTab,
    addTabToForm,
    deleteTabFromForm,
    handleUpdateTabLabel,

    // Funções de criação de planos
    handleSubmitCreatePlan,
    handleSubmitUpdatePlan,

    // Funções de edição e deleção de planos
    handleEditPlan,
    handleDeletePlanWithConfirmation,
    isDeletingPlan: deletePlanMutation.isPending,

    // Dependências
    selectedClient,
    products: products || [],
    users: users || [],
    isCreating: createPlanMutation.isPending,
  };
};

